import axios from "axios";
import React, { useEffect, useState } from "react";
import { flow, get, isString, trim } from "lodash/fp";
import {
  Button,
  Checkbox,
  TextField,
  Typography,
  Grid,
} from "@material-ui/core";
import Autocomplete from "@mui/material/Autocomplete";
import { FormattedMessage } from "react-intl";

const Form = (props) => {
  const { orgId } = props;

  const [isSubmitButtonDisabled, setSubmitButtonDisabled] = useState(false);
  const [isTermsAccepted, setTermsAccepted] = useState(false);
  const [organisation, setOrganisation] = useState(undefined);
  const [organisations, setOrganisations] = useState(null);
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState("");

  // Event handlers
  const onNameChange = flow([get("target.value"), setName]);
  const onLastNameChange = flow([get("target.value"), setLastName]);
  const onTermsAcceptedChange = flow([get("target.checked"), setTermsAccepted]);

  // If no organisation was specified, fetch the list of orgs...
  useEffect(() => {
    if (!organisations) {
      // Fetch the list of organisations from the API (populates a dropdown box)
      return axios.get(`/api/play-it-safe/organisations`).then((resp) => {
        setOrganisations(resp.data);
      });
    }
  }, [organisations, orgId]);

  useEffect(() => {
    if (organisation) {
      // Fetch the list of organisations from the API (populates a dropdown box)
      return axios
        .get(`/api/play-it-safe/${organisation}/departments`)
        .then((resp) => setCompanies(resp.data));
    } else {
      setCompanies([]);
    }
  }, [organisation]);

  useEffect(() => {
    // Checks if the string is not empty after removing whitespaces
    const isValidString = (value) => isString(value) && trim(value).length > 0;
    const isAllInputsValid =
      isTermsAccepted &&
      isValidString(name) &&
      isValidString(lastName) &&
      isValidString(company) &&
      isValidString(organisation);
    setSubmitButtonDisabled(!isAllInputsValid);
  }, [name, lastName, company, isTermsAccepted, organisation]);

  const onButtonClick = () => {
    // Generate a play-it-once link (or re-use existing one), follow the redirect...
    const nameEnc = encodeURIComponent(name + " " + lastName);
    const companyEnc = encodeURIComponent(company);
    const urlToNavigate = `/api/play-it-safe/${organisation}/play?name=${nameEnc}&department=${companyEnc}&redirect=true`;
    window.location.replace(urlToNavigate);
  };

  return (
    <div className="form">
      <Typography variant="h6">
        <FormattedMessage id="form.header" />
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12}>
          {organisations?.length ? (
            <Autocomplete
              fullWidth
              label={<FormattedMessage id="form.org" />}
              variant="outlined"
              options={organisations}
              defaultValue={organisations?.find((o) => o.id === orgId)}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Please enter the site or location"
                />
              )}
              onChange={(_, newValue) => setOrganisation(newValue?.id)}
            />
          ) : (
            <TextField
              fullWidth
              disabled
              label={<FormattedMessage id="busy.org" />}
              variant="outlined"
              value={organisation}
              placeholder="Please enter the site or location"
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={<FormattedMessage id="form.name" />}
            variant="outlined"
            value={name}
            placeholder="Jane"
            onChange={onNameChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={<FormattedMessage id="form.lastname" />}
            variant="outlined"
            value={lastName}
            placeholder="Doe"
            onChange={onLastNameChange}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            fullWidth
            freeSolo
            disabled={!companies}
            label={<FormattedMessage id="form.company" />}
            variant="outlined"
            options={companies}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Contractor Company Name"
                onBlur={(e) => setCompany(e.target.value)}
              />
            )}
            onChange={(_, newValue) => setCompany(newValue)}
          />
        </Grid>
      </Grid>
      <div className="checkboxContainer">
        <Checkbox
          checked={isTermsAccepted}
          className="smallCheckbox"
          name="checkedA"
          onChange={onTermsAcceptedChange}
        />
        <p>
          <FormattedMessage id="form.privacy.prefix" />{" "}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://playitsafe.eu/privacypolicy/"
          >
            Play it Safe
          </a>{" "}
          <FormattedMessage id="common.and" />{" "}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.borealisgroup.com/legal/privacy-statement"
          >
            Borealis
          </a>{" "}
          <FormattedMessage id="form.privacy.suffix" />
        </p>
      </div>

      <div className="buttonContainer">
        <Button
          disabled={isSubmitButtonDisabled}
          onClick={onButtonClick}
          variant="contained"
        >
          <FormattedMessage id="form.participate" />
        </Button>
      </div>
    </div>
  );
};

export default Form;
